import moment from "moment";
import { useHistory } from "react-router";

import { useGetIsLegalEntity } from "~/v2/api/hooks";
import type { OptionsApprovalStatus } from "~/v2/api/rest/account";
import { Path } from "~/v2/path";
import { useIsOptionsOnboardingAvailable } from "../../optionsOptIn/util";
import { FeatureCard } from "./FeatureCard";

type GetCopyArgs = {
  canApplyForOptions: boolean;
  approvedLevel: number;
  effectiveLevel: number;
  optionsApprovalStatus: OptionsApprovalStatus | undefined;
  isLegalEntity: boolean;
};

type GetCopyReturn = { btn: string; body: string; path: string | null };

const getCopy = ({
  effectiveLevel,
  canApplyForOptions,
  approvedLevel,
  isLegalEntity,
  optionsApprovalStatus,
}: GetCopyArgs): GetCopyReturn => {
  const optionsOnboardingPage = isLegalEntity
    ? Path.ROUTE_OPTIONS_OPT_IN_ENTITY_ACCOUNT
    : Path.ROUTE_OPTIONS_OPT_IN_FINANCIAL_PROFILE;

  // has highest approved level
  if (approvedLevel === 3) {
    return {
      btn: "View Configuration",
      body:
        effectiveLevel !== approvedLevel
          ? "Options trading is approved, with the option configuration applied"
          : "Options trading is enabled.",
      path: Path.ROUTE_CONFIGURATION,
    };
  }

  if (!canApplyForOptions) {
    // Pending, do not show re-apply date
    if (optionsApprovalStatus?.OptionsRequestStatus === "PENDING") {
      return {
        btn: "View Configuration",
        body: "Your application is going through the approval process",
        path: Path.ROUTE_CONFIGURATION,
      };
    }

    return {
      btn: "View Configuration",
      body: !optionsApprovalStatus
        ? "Options trading is not enabled."
        : `Upgrade your Options Trading Level! To upgrade, re-apply on ${moment(
            optionsApprovalStatus.ReapplyAvailableDate,
          ).format("MMMM Do")}`,
      path: Path.ROUTE_CONFIGURATION,
    };
  }
  // Following can apply

  // Higher level possible
  if (approvedLevel < 3) {
    return {
      btn: "Upgrade your Options Level",
      body: "Upgrade your Options Trading level!",
      path: optionsOnboardingPage,
    };
  }

  // Has no level and can apply
  if (!effectiveLevel) {
    return {
      btn: "Apply for Options",
      body: "Options trade is not enabled",
      path: optionsOnboardingPage,
    };
  }

  return {
    btn: "View Configuration",
    body: "Options trading is enabled.",
    path: Path.ROUTE_CONFIGURATION,
  };
};

export const OptionsCard = () => {
  const history = useHistory();

  const { isLegalEntity } = useGetIsLegalEntity();
  const {
    isOptionsOnboardingAvailable: isOptionsApplyAvailable,
    optionsApprovalStatus,
    effectiveOptionsLevel,
    optionsApprovedLevel,
  } = useIsOptionsOnboardingAvailable({
    allowLegalEntities: true,
  });

  const { btn, body, path } = getCopy({
    isLegalEntity: isLegalEntity ?? false,
    canApplyForOptions: isOptionsApplyAvailable ?? false,
    approvedLevel: optionsApprovedLevel ?? 0,
    effectiveLevel: effectiveOptionsLevel,
    optionsApprovalStatus: optionsApprovalStatus,
  });

  const onClick = () => path && history.push(path);

  return (
    <FeatureCard
      title="Options"
      badge={
        effectiveOptionsLevel > 0
          ? { variant: "blue", text: `Level ${effectiveOptionsLevel}` }
          : optionsApprovalStatus?.OptionsRequestStatus === "PENDING"
            ? { variant: "gray", text: "Pending" }
            : { variant: "gray", text: "Disabled" }
      }
      body={body}
      button={{
        text: btn,
        onClick,
        disabled: !path,
      }}
    />
  );
};
